import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Download, FileBox, Plus, Sparkles, Trash2, Upload } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { Intervention, Specification, ToolkitAsset } from "@/lib/domain";

const FORMATS = ["ifc", "dwg", "revit", "sketchup", "texture", "document", "other"];
const FORMAT_LABEL: Record<string, string> = {
  ifc: "IFC",
  dwg: "DWG",
  revit: "Revit",
  sketchup: "SketchUp",
  texture: "Texture",
  document: "PDF",
  other: "File",
};
const FINISHES = [
  "spatolato",
  "travertino",
  "cemento_industriale",
  "nuvolato",
  "pietra_naturale",
  "calce_antica",
];
const FINISH_LABEL: Record<string, string> = {
  spatolato: "Effetto Spatolato",
  travertino: "Effetto Travertino",
  cemento_industriale: "Cemento Industriale",
  nuvolato: "Nuvolato Artistico",
  pietra_naturale: "Effetto Pietra Naturale",
  calce_antica: "Effetto Calce Antica",
};

const TEXT = {
  it: {
    kicker: "Toolkit tecnico",
    title: "Strumenti per progettisti",
    downloads: "Modelli e materiali",
    capitolato: "Capitolato",
    samples: "Campioni",
    all: "Tutti",
    download: "Scarica",
    downloadsCount: "download",
    publish: "Pubblica",
    unpublish: "Nascondi",
    upload: "Carica un file",
    uploadHint:
      "IFC, DWG, RVT, SKP o pacchetti texture. Il file viene salvato nello storage del progetto.",
    title_field: "Titolo",
    format: "Formato",
    finish: "Finitura",
    version: "Versione",
    file: "File",
    send: "Carica",
    sending: "Caricamento…",
    empty: "Nessun file pubblicato.",
    emptyAdmin: "Nessun file caricato.",
    copy: "Copia",
    copied: "Copiato",
    specNew: "Nuovo capitolato",
    body: "Testo del capitolato",
    locale: "Lingua",
    save: "Salva",
    sampleIntro:
      "Richiedi campioni fisici delle finiture. La richiesta arriva al team VERSUS, che organizza la spedizione.",
    selectFinishes: "Finiture richieste",
    area: "Superficie indicativa (m²)",
    address: "Indirizzo di spedizione",
    studio: "Studio",
    phone: "Telefono",
    project: "Progetto",
    notes: "Note",
    request: "Richiedi campioni",
    requested: "Richiesta inviata. Il team VERSUS ti contatterà.",
    recent: "Richieste recenti",
  },
  en: {
    kicker: "Technical toolkit",
    title: "Tools for designers",
    downloads: "Models & materials",
    capitolato: "Specifications",
    samples: "Samples",
    all: "All",
    download: "Download",
    downloadsCount: "downloads",
    publish: "Publish",
    unpublish: "Hide",
    upload: "Upload a file",
    uploadHint:
      "IFC, DWG, RVT, SKP or texture packs. Stored in the project's object storage.",
    title_field: "Title",
    format: "Format",
    finish: "Finish",
    version: "Version",
    file: "File",
    send: "Upload",
    sending: "Uploading…",
    empty: "No files published.",
    emptyAdmin: "No files uploaded.",
    copy: "Copy",
    copied: "Copied",
    specNew: "New specification",
    body: "Specification text",
    locale: "Language",
    save: "Save",
    sampleIntro:
      "Request physical samples of the finishes. The request reaches the VERSUS team, who arrange shipping.",
    selectFinishes: "Requested finishes",
    area: "Indicative area (m²)",
    address: "Shipping address",
    studio: "Studio",
    phone: "Phone",
    project: "Project",
    notes: "Notes",
    request: "Request samples",
    requested: "Request sent. The VERSUS team will be in touch.",
    recent: "Recent requests",
  },
  es: {
    kicker: "Toolkit técnico",
    title: "Herramientas para proyectistas",
    downloads: "Modelos y materiales",
    capitolato: "Pliego",
    samples: "Muestras",
    all: "Todos",
    download: "Descargar",
    downloadsCount: "descargas",
    publish: "Publicar",
    unpublish: "Ocultar",
    upload: "Subir un archivo",
    uploadHint:
      "IFC, DWG, RVT, SKP o paquetes de texturas. Se guarda en el almacenamiento del proyecto.",
    title_field: "Título",
    format: "Formato",
    finish: "Acabado",
    version: "Versión",
    file: "Archivo",
    send: "Subir",
    sending: "Subiendo…",
    empty: "Ningún archivo publicado.",
    emptyAdmin: "Ningún archivo subido.",
    copy: "Copiar",
    copied: "Copiado",
    specNew: "Nuevo pliego",
    body: "Texto del pliego",
    locale: "Idioma",
    save: "Guardar",
    sampleIntro:
      "Solicita muestras físicas de los acabados. La solicitud llega al equipo VERSUS, que organiza el envío.",
    selectFinishes: "Acabados solicitados",
    area: "Superficie indicativa (m²)",
    address: "Dirección de envío",
    studio: "Estudio",
    phone: "Teléfono",
    project: "Proyecto",
    notes: "Notas",
    request: "Solicitar muestras",
    requested: "Solicitud enviada. El equipo VERSUS te contactará.",
    recent: "Solicitudes recientes",
  },
} as const;

type Tab = "downloads" | "capitolato" | "samples";

function bytes(n?: number | null): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ToolkitPage() {
  const { locale } = useLocale();
  const { user } = useAuth();
  const tx = TEXT[locale];
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState<Tab>("downloads");
  const [assets, setAssets] = useState<ToolkitAsset[] | null>(null);
  const [specs, setSpecs] = useState<Specification[] | null>(null);
  const [format, setFormat] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const loadAssets = useCallback(async () => {
    setAssets(null);
    try {
      setAssets(await api.toolkitAssets(format ? { format } : {}));
    } catch {
      setAssets([]);
    }
  }, [format]);

  const loadSpecs = useCallback(async () => {
    setSpecs(null);
    try {
      setSpecs(await api.specifications());
    } catch {
      setSpecs([]);
    }
  }, []);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);
  useEffect(() => {
    void loadSpecs();
  }, [loadSpecs]);

  const sampleForm = useMemo(
    () => ({
      finishes: [] as string[],
      areaM2: "",
      shippingAddress: "",
      studio: user?.name ?? "",
      phone: "",
      projectName: "",
      notes: "",
    }),
    [user?.name],
  );
  const [sample, setSample] = useState(sampleForm);

  async function onUpload(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = data.get("file");
    if (!(file instanceof File) || !file.name) return;
    setBusy(true);
    try {
      await api.uploadToolkitAsset(file, {
        title: String(data.get("title") ?? ""),
        format: String(data.get("format") ?? "other"),
        finishEffect: String(data.get("finishEffect") ?? "") || undefined,
        version: String(data.get("version") ?? "") || undefined,
        published: data.get("published") ? "true" : "false",
      });
      form.reset();
      await loadAssets();
    } finally {
      setBusy(false);
    }
  }

  async function onSampleRequest(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.requestSamples({
        finishes: sample.finishes,
        areaM2: sample.areaM2 ? Number(sample.areaM2) : undefined,
        shippingAddress: sample.shippingAddress,
        studio: sample.studio || undefined,
        phone: sample.phone || undefined,
        projectName: sample.projectName || undefined,
        notes: sample.notes || undefined,
      });
      setSent(true);
      setSample(sampleForm);
    } finally {
      setBusy(false);
    }
  }

  async function copySpec(spec: Specification) {
    await navigator.clipboard.writeText(spec.body);
    setCopied(spec.id);
    setTimeout(() => setCopied(null), 1500);
  }

  function downloadSpec(spec: Specification) {
    const blob = new Blob([spec.body], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${spec.title.replace(/[^A-Za-z0-9._-]/g, "_")}-${spec.locale}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "downloads", label: tx.downloads },
    { id: "capitolato", label: tx.capitolato },
    { id: "samples", label: tx.samples },
  ];

  return (
    <AppShell>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">{tx.title}</h1>

      <div className="mt-5 flex gap-1 rounded-full border border-white/15 p-0.5">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setTab(tb.id)}
            className={cn(
              "rounded-full px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] transition",
              tab === tb.id ? "bg-white/15 text-paper" : "text-ice/60",
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------- downloads */}
      {tab === "downloads" && (
        <>
          <div className="mt-5 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setFormat("")}
              className={cn(
                "rounded-full border px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.12em]",
                format === "" ? "border-magenta text-paper" : "border-white/15 text-ice/60",
              )}
            >
              {tx.all}
            </button>
            {FORMATS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={cn(
                  "rounded-full border px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.12em]",
                  format === f ? "border-magenta text-paper" : "border-white/15 text-ice/60",
                )}
              >
                {FORMAT_LABEL[f]}
              </button>
            ))}
          </div>

          {isAdmin && (
            <form
              onSubmit={onUpload}
              className="glass-panel-strong mt-4 grid gap-3 p-5 sm:grid-cols-3"
            >
              <div className="sm:col-span-3">
                <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.16em] text-goldbright">
                  <Upload className="h-3.5 w-3.5" />
                  {tx.upload}
                </div>
                <p className="mt-1 text-[0.68rem] text-ice/55">{tx.uploadHint}</p>
              </div>
              <label className="block sm:col-span-2">
                <span className="field-label">{tx.title_field}</span>
                <input name="title" className="field" required />
              </label>
              <label className="block">
                <span className="field-label">{tx.format}</span>
                <select name="format" className="field" defaultValue="ifc">
                  {FORMATS.map((f) => (
                    <option key={f} value={f} className="bg-night">
                      {FORMAT_LABEL[f]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="field-label">{tx.finish}</span>
                <select name="finishEffect" className="field" defaultValue="">
                  <option value="" className="bg-night">
                    —
                  </option>
                  {FINISHES.map((f) => (
                    <option key={f} value={f} className="bg-night">
                      {FINISH_LABEL[f]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="field-label">{tx.version}</span>
                <input name="version" className="field" placeholder="2026.1" />
              </label>
              <label className="block">
                <span className="field-label">{tx.file}</span>
                <input name="file" type="file" className="field" required />
              </label>
              <label className="flex items-center gap-2 text-[0.66rem] uppercase tracking-[0.12em] text-ice/60">
                <input name="published" type="checkbox" className="accent-magenta" />
                {tx.publish}
              </label>
              <div className="sm:col-span-3">
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
                >
                  <Plus className="h-3 w-3" />
                  {busy ? tx.sending : tx.send}
                </button>
              </div>
            </form>
          )}

          <div className="mt-4 grid gap-3">
            {assets === null && <p className="text-sm text-ice/60">…</p>}
            {assets?.length === 0 && (
              <p className="text-sm text-ice/60">
                {isAdmin ? tx.emptyAdmin : tx.empty}
              </p>
            )}
            {assets?.map((a) => (
              <article key={a.id} className="glass-panel-strong flex flex-wrap items-center gap-4 p-4">
                <FileBox className="h-5 w-5 shrink-0 text-goldbright" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-serif text-[0.95rem] text-paper">{a.title}</span>
                    <span className="rounded-full border border-white/15 px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.14em] text-goldbright">
                      {FORMAT_LABEL[a.format] ?? a.format}
                    </span>
                    {a.finishEffect && (
                      <span className="text-[0.62rem] text-ice/55">
                        {FINISH_LABEL[a.finishEffect] ?? a.finishEffect}
                      </span>
                    )}
                    {!a.published && (
                      <span className="text-[0.58rem] uppercase tracking-[0.12em] text-pink">
                        {tx.unpublish}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 text-[0.64rem] text-ice/55">
                    {a.version && <span>v{a.version}</span>}
                    <span>{bytes(a.sizeBytes)}</span>
                    <span className="num">
                      {a.downloadCount} {tx.downloadsCount}
                    </span>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        await api.updateToolkitAsset(a.id, { published: !a.published });
                        await loadAssets();
                      }}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-[0.56rem] font-semibold uppercase tracking-[0.12em] text-ice/70"
                    >
                      {a.published ? tx.unpublish : tx.publish}
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        await api.deleteToolkitAsset(a.id);
                        await loadAssets();
                      }}
                      className="rounded-full border border-rose/50 px-2.5 py-1.5 text-pink"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}

                <a
                  href={api.toolkitAssetUrl(a.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-magenta px-4 py-2 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-paper hover:brightness-110"
                >
                  <Download className="h-3 w-3" />
                  {tx.download}
                </a>
              </article>
            ))}
          </div>
        </>
      )}

      {/* --------------------------------------------------- capitolato */}
      {tab === "capitolato" && (
        <>
          {isAdmin && <SpecForm onSaved={loadSpecs} locale={locale} tx={tx} />}

          <div className="mt-4 grid gap-3">
            {specs === null && <p className="text-sm text-ice/60">…</p>}
            {specs?.length === 0 && <p className="text-sm text-ice/60">{tx.empty}</p>}
            {specs?.map((s) => (
              <article key={s.id} className="glass-panel-strong p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Sparkles className="h-4 w-4 text-goldbright" />
                  <span className="font-serif text-[0.95rem] text-paper">{s.title}</span>
                  <span className="rounded-full border border-white/15 px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.14em] text-ice/60">
                    {s.locale}
                  </span>
                  {s.version && (
                    <span className="text-[0.62rem] text-ice/55">v{s.version}</span>
                  )}
                  {!s.published && (
                    <span className="text-[0.58rem] uppercase tracking-[0.12em] text-pink">
                      {tx.unpublish}
                    </span>
                  )}
                </div>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-black/20 p-3 text-[0.7rem] leading-relaxed text-ice/80">
                  {s.body}
                </pre>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copySpec(s)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-ice/75"
                  >
                    <Copy className="h-3 w-3" />
                    {copied === s.id ? tx.copied : tx.copy}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadSpec(s)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-magenta px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-paper"
                  >
                    <Download className="h-3 w-3" />
                    .txt
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          await api.updateSpecification(s.id, { published: !s.published });
                          await loadSpecs();
                        }}
                        className="rounded-full border border-white/15 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-ice/70"
                      >
                        {s.published ? tx.unpublish : tx.publish}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          await api.deleteSpecification(s.id);
                          await loadSpecs();
                        }}
                        className="rounded-full border border-rose/50 px-2.5 py-1.5 text-pink"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {/* ----------------------------------------------------- samples */}
      {tab === "samples" && (
        <div className="mt-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <form onSubmit={onSampleRequest} className="glass-panel-strong p-5">
            <p className="max-w-xl text-[0.78rem] leading-relaxed text-ice/70">
              {tx.sampleIntro}
            </p>

            <div className="mt-4">
              <span className="field-label">{tx.selectFinishes}</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {FINISHES.map((f) => {
                  const on = sample.finishes.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() =>
                        setSample((prev) => ({
                          ...prev,
                          finishes: on
                            ? prev.finishes.filter((x) => x !== f)
                            : [...prev.finishes, f],
                        }))
                      }
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.1em] transition",
                        on
                          ? "border-magenta bg-magenta/20 text-paper"
                          : "border-white/15 text-ice/60",
                      )}
                    >
                      {FINISH_LABEL[f]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="field-label">{tx.area}</span>
                <input
                  className="field num"
                  inputMode="decimal"
                  value={sample.areaM2}
                  onChange={(e) => setSample({ ...sample, areaM2: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="field-label">{tx.project}</span>
                <input
                  className="field"
                  value={sample.projectName}
                  onChange={(e) => setSample({ ...sample, projectName: e.target.value })}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="field-label">{tx.address}</span>
                <input
                  className="field"
                  required
                  value={sample.shippingAddress}
                  onChange={(e) =>
                    setSample({ ...sample, shippingAddress: e.target.value })
                  }
                />
              </label>
              <label className="block">
                <span className="field-label">{tx.studio}</span>
                <input
                  className="field"
                  value={sample.studio}
                  onChange={(e) => setSample({ ...sample, studio: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="field-label">{tx.phone}</span>
                <input
                  className="field num"
                  value={sample.phone}
                  onChange={(e) => setSample({ ...sample, phone: e.target.value })}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="field-label">{tx.notes}</span>
                <textarea
                  className="field min-h-20"
                  value={sample.notes}
                  onChange={(e) => setSample({ ...sample, notes: e.target.value })}
                />
              </label>
            </div>

            {sent && <p className="mt-3 text-xs text-green">{tx.requested}</p>}

            <button
              type="submit"
              disabled={busy || sample.finishes.length === 0 || !sample.shippingAddress}
              className="mt-4 rounded-full bg-magenta px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper disabled:opacity-50"
            >
              {tx.request}
            </button>
          </form>

          <div className="glass-panel-strong p-5">
            <div className="brand-kicker">{tx.recent}</div>
            <SampleList />
          </div>
        </div>
      )}
    </AppShell>
  );
}

function SampleList() {
  const { locale } = useLocale();
  const [rows, setRows] = useState<Intervention[] | null>(null);

  useEffect(() => {
    api
      .sampleRequests()
      .then(setRows)
      .catch(() => setRows([]));
  }, []);

  if (rows === null) return <p className="mt-2 text-[0.72rem] text-ice/60">…</p>;
  if (rows.length === 0)
    return <p className="mt-2 text-[0.72rem] text-ice/60">—</p>;

  return (
    <ul className="mt-2 grid gap-2">
      {rows.slice(0, 6).map((r) => (
        <li key={r.id} className="rounded-lg border border-white/10 bg-white/5 p-2.5">
          <div className="text-[0.72rem] text-paper">
            {r.details?.finishes?.join(", ") ?? r.description}
          </div>
          <div className="mt-0.5 text-[0.6rem] text-ice/55">
            {r.contactName} · {r.requestedAt.slice(0, 10)} ·{" "}
            {r.status === "closed"
              ? locale === "it"
                ? "chiusa"
                : locale === "es"
                  ? "cerrada"
                  : "closed"
              : r.status}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SpecForm({
  onSaved,
  locale,
  tx,
}: {
  onSaved: () => Promise<void> | void;
  locale: "it" | "en" | "es";
  tx: Record<keyof (typeof TEXT)["it"], string>;
}) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ice/70"
      >
        + {tx.specNew}
      </button>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const data = new FormData(form);
        setBusy(true);
        try {
          await api.createSpecification({
            title: String(data.get("title") ?? ""),
            locale: String(data.get("locale") ?? locale),
            finishEffect: String(data.get("finishEffect") ?? "") || undefined,
            version: String(data.get("version") ?? "") || undefined,
            body: String(data.get("body") ?? ""),
            published: data.get("published") ? true : false,
          });
          form.reset();
          setOpen(false);
          await onSaved();
        } finally {
          setBusy(false);
        }
      }}
      className="glass-panel-strong mt-4 grid gap-3 p-5 sm:grid-cols-3"
    >
      <label className="block sm:col-span-2">
        <span className="field-label">{tx.title_field}</span>
        <input name="title" className="field" required />
      </label>
      <label className="block">
        <span className="field-label">{tx.locale}</span>
        <select name="locale" className="field" defaultValue={locale}>
          {(["it", "en", "es"] as const).map((l) => (
            <option key={l} value={l} className="bg-night">
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="field-label">{tx.finish}</span>
        <select name="finishEffect" className="field" defaultValue="">
          <option value="" className="bg-night">
            —
          </option>
          {FINISHES.map((f) => (
            <option key={f} value={f} className="bg-night">
              {FINISH_LABEL[f]}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="field-label">{tx.version}</span>
        <input name="version" className="field" />
      </label>
      <label className="flex items-center gap-2 text-[0.66rem] uppercase tracking-[0.12em] text-ice/60">
        <input name="published" type="checkbox" className="accent-magenta" />
        {tx.publish}
      </label>
      <label className="block sm:col-span-3">
        <span className="field-label">{tx.body}</span>
        <textarea name="body" className="field min-h-32" required />
      </label>
      <div className="sm:col-span-3">
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
        >
          {busy ? "…" : tx.save}
        </button>
      </div>
    </form>
  );
}
